// src/components/DataContext.js

import React, { createContext, useState, useEffect, useMemo } from 'react';
import { firestore, auth } from './firebase'; // Adjust the import path if necessary
import {
  collection,
  getDocs,
  doc,
  getDoc,
  orderBy,
  setDoc,
  query,
} from 'firebase/firestore';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export const DataContext = createContext();

export const DataProvider = ({ children }) => {
  const queryClient = useQueryClient();

  // User Data State
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState({
    fullName: '',
    photoURL: '',
  });

  // Sorting State
  const [sortField, setSortField] = useState('nombreApellido'); // Default sort field
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Fetch User Role and Data
  const fetchUserRoleAndData = async (user) => {
    if (user && firestore) {
      const roleDocRef = doc(firestore, 'roles', user.uid);
      const roleSnapshot = await getDoc(roleDocRef);
      if (roleSnapshot.exists()) {
        const roleData = roleSnapshot.data();
        setUserRole(roleData.role);
        setUserData({
          fullName: roleData.fullName || user.email,
          photoURL: roleData.photoURL || '',
        });
      } else {
        setUserRole('user');
        setUserData({
          fullName: user.email,
          photoURL: '',
        });
      }
    } else {
      setUserRole(null);
      setUserData({
        fullName: '',
        photoURL: '',
      });
    }
  };

  useEffect(() => {
    // Set up authentication state listener
    const unsubscribe = auth.onAuthStateChanged((user) => {
      console.log('Authentication State Changed:', user);
      fetchUserRoleAndData(user);
      if (user) {
        // Invalidate queries to fetch fresh data that require authentication
        queryClient.invalidateQueries(['allRecords']);
      } else {
        // Remove queries that require authentication
        queryClient.removeQueries(['allRecords']);
        // Do not clear appSettings so that unauthenticated users can access it
      }
    });

    return () => unsubscribe();
  }, [queryClient]);

  // Fetch appSettings using useQuery without staleTime and cacheTime
  const {
    data: appSettingsData,
    isLoading: appSettingsLoading,
    error: appSettingsError,
    refetch: refetchAppSettings,
  } = useQuery({
    queryKey: ['appSettings'],
    queryFn: async () => {
      const settingsDocRef = doc(firestore, 'settings', 'appSettings');
      const docSnapshot = await getDoc(settingsDocRef);
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        console.log('Fetched appSettings:', data);
        return data;
      } else {
        const defaultSettings = {
          logoUrl: '',
          primaryColor: '#bb8300',
          language: 'en',
        };
        await setDoc(settingsDocRef, defaultSettings);
        console.log('Created default appSettings:', defaultSettings);
        return defaultSettings;
      }
    },
    initialData: {
      logoUrl: '',
      primaryColor: '#bb8300',
      language: 'en',
    },
    enabled: !!firestore, // Removed the dependency on auth.currentUser
    onError: (err) => {
      console.error('Error fetching appSettings:', err);
    },
  });

  // Fetch all records once
  const {
    data: allRecordsData,
    isLoading: allRecordsLoading,
    error: allRecordsError,
    refetch: refetchAllRecords,
  } = useQuery({
    queryKey: ['allRecords'],
    queryFn: async () => {
      const recordsRef = collection(firestore, 'records');
      const q = query(recordsRef, orderBy('nombre', 'asc')); // Initial sort by 'nombre'
      const snapshot = await getDocs(q);
      const fetchedData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log('Fetched all records:', fetchedData);
      return fetchedData;
    },
    enabled: !!firestore && !!auth.currentUser, // Remains dependent on authentication
    onError: (err) => {
      console.error('Error fetching all records:', err);
    },
  });

  // Sorting Records Client-Side
  const sortedRecords = useMemo(() => {
    if (!allRecordsData) return [];

    const sorted = [...allRecordsData].sort((a, b) => {
      if (sortField === 'nombreApellido') {
        // Sort by 'nombre' and then 'apellido'
        const nameComparison = a.nombre.localeCompare(b.nombre);
        if (nameComparison !== 0) {
          return sortOrder === 'asc' ? nameComparison : -nameComparison;
        } else {
          const surnameComparison = a.apellido.localeCompare(b.apellido);
          return sortOrder === 'asc' ? surnameComparison : -surnameComparison;
        }
      } else {
        const aField = a[sortField];
        const bField = b[sortField];

        // Handle undefined or null fields
        if (aField == null && bField == null) return 0;
        if (aField == null) return sortOrder === 'asc' ? -1 : 1;
        if (bField == null) return sortOrder === 'asc' ? 1 : -1;

        // Compare based on data type
        if (typeof aField === 'string' && typeof bField === 'string') {
          return sortOrder === 'asc'
            ? aField.localeCompare(bField)
            : bField.localeCompare(aField);
        }

        if (aField instanceof Date && bField instanceof Date) {
          return sortOrder === 'asc' ? aField - bField : bField - aField;
        }

        // Default comparison for numbers or other types
        if (aField < bField) return sortOrder === 'asc' ? -1 : 1;
        if (aField > bField) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      }
    });
    return sorted;
  }, [allRecordsData, sortField, sortOrder]);

  // Mutation to update appSettings
  const updateAppSettingsMutation = useMutation({
    mutationFn: async (newSettings) => {
      const settingsDocRef = doc(firestore, 'settings', 'appSettings');
      await setDoc(settingsDocRef, newSettings, { merge: true });
    },
    onSuccess: () => {
      // After mutation, invalidate and refetch appSettings
      queryClient.invalidateQueries(['appSettings']);
    },
    onError: (err) => {
      console.error('Error updating appSettings:', err);
    },
  });

  // Function to update appSettings
  const updateSettings = async (newSettings) => {
    await updateAppSettingsMutation.mutateAsync(newSettings);
  };

  // Function to set sorting parameters
  const setSorting = (field) => {
    if (sortField === field) {
      // Toggle sort order
      setSortOrder((prevOrder) => (prevOrder === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc'); // Reset to ascending when changing field
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
    // No need to invalidate allRecords query; sorting is handled client-side
  };

  // Function to change page
  const goToPage = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  // Function to handle itemsPerPage change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    // No need to refetch allRecords; pagination is handled client-side
  };

  // Calculate totalPages based on sortedRecords
  const totalPages = sortedRecords
    ? Math.ceil(sortedRecords.length / itemsPerPage)
    : 1;

  // Generate page numbers
  const pageNumbers = useMemo(() => {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }, [totalPages]);

  // Expose data and functions via context
  return (
    <DataContext.Provider
      value={{
        // Records data
        sortedRecords: sortedRecords || [],
        allRecordsLoading,
        allRecordsError,
        refetchAllRecords,

        // Pagination
        currentPage,
        totalPages,
        pageNumbers,
        onPrev: () => goToPage(currentPage - 1),
        onNext: () => goToPage(currentPage + 1),
        onPageClick: (page) => goToPage(page),
        itemsPerPage,
        onItemsPerPageChange: handleItemsPerPageChange,

        // User data
        userRole,
        userData,

        // App settings
        appSettings: appSettingsData,
        appSettingsLoading,
        appSettingsError,
        updateSettings,
        refetchAppSettings,

        // Sorting
        sortField,
        sortOrder,
        setSorting,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};

export default DataProvider;
